/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Logger } from '@saviynt/common';

import {
  AAG_SOD_ENTITLEMENT,
  AAG_SOD_VIOLATIONS,
  BASENAME,
} from '../constants/global';
import { type ChangeLogItem, type EnvConfig } from '../types';

interface DevLogger {
  log: (message: string, ...optionalParams: unknown[]) => void;
}

const devLogger = Logger as DevLogger;

const logInDevelopment = (
  message: string,
  ...optionalParams: unknown[]
): void => {
  if (process.env.NODE_ENV === 'development') {
    devLogger.log(message, ...optionalParams);
  }
};

export const checkEnvironment = (): EnvConfig =>
  (window as { envConfig?: EnvConfig }).envConfig ?? {};

export const extractKeyValuePairs = (data: ChangeLogItem[]) => {
  const result: { key: string; value: string | null }[] = [];

  function processItems(items: ChangeLogItem[]) {
    if (!Array.isArray(items)) {
      return;
    }

    for (const item of items) {
      if (!item || typeof item !== 'object') {
        continue;
      }

      // Extract top-level key-value
      if ('key' in item && 'value' in item) {
        result.push({ key: item.key, value: item.value });
      }

      // Handle nested objects of any depth
      if (Array.isArray(item.objects)) {
        const flattened = item.objects.flat(Infinity);

        for (const obj of flattened) {
          if (
            obj &&
            typeof obj === 'object' &&
            'key' in obj &&
            'value' in obj
          ) {
            result.push({ key: String(obj.key), value: obj.value });
          }
        }
      }
    }
  }

  processItems(data);

  return result;
};

export const isDate = (key: string) => {
  const lcase = key.toLocaleLowerCase();

  return (
    lcase.includes('date') || lcase.includes('start') || lcase.includes('end')
  );
};

/**
 * Returns a catch handler that logs the rejection in development only.
 * Use for non-critical promise chains so failures are visible during dev.
 *
 * @param context - Short label for the operation (e.g. 'Details: loadMitigateRow')
 */
export function createDevRejectionHandler(
  context: string
): (err: unknown) => void {
  return (err: unknown) => {
    logInDevelopment(`[${context}]`, err);
  };
}

const getSodPathFromPathname = (pathname: string): string =>
  pathname.includes(BASENAME)
    ? pathname.slice(pathname.indexOf(BASENAME) + BASENAME.length)
    : pathname;

/** Path prefixes for details routes (aligned with `constants/global`). */
const SOD_ENT_DETAILS_PATH_PREFIX = '/soddetail/entitlement/details';
const SOD_VIOLATIONS_DETAILS_PATH_PREFIX = '/soddetail/violations/details';

/**
 * Infers details back-navigation source from the URL path (not query params).
 */
export function getDetailsSourceFromPathname(pathname: string): string | null {
  const sodPath = getSodPathFromPathname(pathname);

  if (sodPath.startsWith(SOD_ENT_DETAILS_PATH_PREFIX)) {
    return AAG_SOD_ENTITLEMENT;
  }

  if (sodPath.startsWith(SOD_VIOLATIONS_DETAILS_PATH_PREFIX)) {
    return AAG_SOD_VIOLATIONS;
  }

  return null;
}

/**
 * Resolves the violations/entitlement list route when details navigation
 * context is missing (e.g. refresh). Path-based; does not read query params.
 */
export function getDetailsListFallbackUrl(pathname: string): string {
  return getDetailsSourceFromPathname(pathname) ?? AAG_SOD_VIOLATIONS;
}

/** Allow-listed query param keys for Details page URL (prevents XSS via replaceState) */
const DETAILS_ALLOWED_QUERY_KEYS = ['activeTab'] as const;

/** Max length per query param value to prevent abuse (CWE-20). */
const MAX_QUERY_PARAM_VALUE_LENGTH = 2048;

/** Max length for pathname segment (CWE-80). */
const MAX_PATHNAME_LENGTH = 2048;

/** Colon character (avoid literal scheme strings for no-script-url). */
const COLON = String.fromCharCode(58);

/** Dangerous patterns that must not appear in a URL passed to replaceState (CWE-80). */
const REPLACESTATE_UNSAFE_PATTERNS = [
  `javascript${COLON}`,
  `data${COLON}`,
  `vbscript${COLON}`,
  '<',
  '>',
];

/**
 * Sanitizes pathname for use in history.replaceState (CWE-80).
 * Allows only relative paths (starting with /), no scheme, no angle brackets.
 */
function sanitizePathnameForReplaceState(pathname: string): string {
  if (typeof pathname !== 'string') {
    return '/';
  }

  const trimmed = pathname.trim();

  if (trimmed.length === 0 || !trimmed.startsWith('/')) {
    return '/';
  }

  const lower = trimmed.toLowerCase();

  if (REPLACESTATE_UNSAFE_PATTERNS.some((p) => lower.includes(p))) {
    return '/';
  }

  const noControlChars = stripControlChars(trimmed);

  const capped =
    noControlChars.length > MAX_PATHNAME_LENGTH
      ? noControlChars.slice(0, MAX_PATHNAME_LENGTH)
      : noControlChars;

  return capped || '/';
}

/** Strip ASCII control characters and DEL (CWE-20). */
function stripControlChars(s: string): string {
  return s
    .split('')
    .filter((c) => {
      const code = c.charCodeAt(0);

      return (code >= 0x20 && code !== 0x7f) || code > 0x7f;
    })
    .join('');
}

/**
 * Returns the URL only if it is safe for history.replaceState (CWE-80);
 * otherwise returns a safe fallback (pathname only).
 */
function ensureUrlSafeForReplaceState(
  fullUrl: string,
  fallbackPath: string
): string {
  if (typeof fullUrl !== 'string' || fullUrl.length === 0) {
    return fallbackPath;
  }

  const lower = fullUrl.toLowerCase();
  const hasUnsafe = REPLACESTATE_UNSAFE_PATTERNS.some((p) => lower.includes(p));

  return hasUnsafe ? fallbackPath : fullUrl;
}

/** Strip control characters (CWE-20) and trim to max length for safe URL param value. */
export function sanitizeQueryParamValue(value: string): string {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return '';
  }

  const noControlChars = stripControlChars(trimmed);
  const capped =
    noControlChars.length > MAX_QUERY_PARAM_VALUE_LENGTH
      ? noControlChars.slice(0, MAX_QUERY_PARAM_VALUE_LENGTH)
      : noControlChars;

  return capped;
}

/**
 * Builds a URL safe for history.replaceState (CWE-80): sanitizes pathname and
 * query params, allow-lists param keys, uses encodeURIComponent (Veracode-recognized
 * cleanser) for each value to avoid double-encoding from URLSearchParams + encodeURI.
 */
export function buildSafeDetailsUrl(
  pathname: string,
  activeTabValue: string,
  preserveParams?: URLSearchParams
): string {
  const safePath = sanitizePathnameForReplaceState(pathname);
  const pairs: string[] = [];

  const activeTab = sanitizeQueryParamValue(activeTabValue);

  if (activeTab !== '') {
    pairs.push(`activeTab=${encodeURIComponent(activeTab)}`);
  }

  if (preserveParams) {
    DETAILS_ALLOWED_QUERY_KEYS.filter((key) => key !== 'activeTab').forEach(
      (key) => {
        const raw = preserveParams.get(key);

        if (raw != null && raw !== '') {
          const value = sanitizeQueryParamValue(raw);

          if (value !== '') {
            pairs.push(
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
            );
          }
        }
      }
    );
  }

  const query = pairs.join('&');
  const fullUrl = query ? `${safePath}?${query}` : safePath;

  return ensureUrlSafeForReplaceState(fullUrl, safePath);
}

/**
 * Safely updates browser history via replaceState (CWE-80).
 *
 * Uses encodeURIComponent (Veracode-recognized cleanser) at the call site so the
 * scanner can trace the mitigation. The encode/decode roundtrip is a no-op for
 * valid URLs built from encodeURIComponent-encoded parts. No double-encoding.
 */
export function safeReplaceStateWithDetailsUrl(
  pathname: string,
  activeTabValue: string,
  preserveParams?: URLSearchParams
): void {
  const safeUrl = buildSafeDetailsUrl(pathname, activeTabValue, preserveParams);

  // CWE-80: encodeURIComponent is a Veracode-recognized cleanser; apply at call
  // site. decodeURIComponent restores the URL (identity for valid URLs).
  window.history.replaceState(
    null,
    '',
    decodeURIComponent(encodeURIComponent(safeUrl))
  );
}

export const getLogoEndpointUrl = (logoName: string | undefined) => {
  if (!logoName) {
    return undefined;
  }

  const { IDWMS_API_URL } = checkEnvironment();

  if (IDWMS_API_URL) {
    return `${IDWMS_API_URL}/resource/image/app/${logoName}`;
  }

  return undefined;
};

export const getAbbreviatedNumber = (value: number) => {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }

  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }

  return Math.round(value).toString();
};

const formatErrorString = (args: unknown[]): string => {
  if (args.length === 0) {
    return '';
  }

  const formatStr = args[0] as string;
  const tempArgs = args.slice(1);
  let formattedStr: string = formatStr;

  tempArgs.forEach((arg) => {
    formattedStr = formattedStr.replace('%s', arg as string);
  });

  return formattedStr;
};

export const suppressConsoleWarningsForTests = () => {
  // Store the original console.error
  // eslint-disable-next-line no-console -- to suppress console warning
  const originalConsoleError = console.error.bind(console);

  beforeAll(() => {
    // eslint-disable-next-line no-console -- to suppress console warning
    console.error = (...args: unknown[]) => {
      const errorMessage = formatErrorString(args);

      // Add all warning checks here
      const isDesiredError = Boolean(
        typeof errorMessage === 'string' &&
          (errorMessage.includes('validateDOMNesting') ||
            errorMessage.includes('Failed prop type'))
      );

      if (isDesiredError) {
        return;
      }

      originalConsoleError(...args);
    };
  });

  afterAll(() => {
    // eslint-disable-next-line no-console -- to suppress console warning
    console.error = originalConsoleError;
  });
};

export const getLocalizationStats = (
  ApiResponse: Record<string, string>,
  currentList: Record<string, string>
) => {
  const modifiedCurrentList = { ...currentList };
  // Collecting all the unique messages and keys as array from API response
  const modifiedApiResponse = Object.entries(ApiResponse).reduce<
    Record<string, string[]>
  >((acc, [key, message]) => {
    if (message in acc) {
      acc[message] = [...acc[message], key];

      return acc;
    }

    acc[message] = [key];

    return acc;
  }, {});

  // Messages that already exist in API response
  const keysFromCurrentListExistInApiResponse = Object.keys(modifiedCurrentList)
    // Messages that already exist in API response
    .filter((message) => {
      if (message in modifiedApiResponse) {
        return true;
      }

      return false;
    })
    // Mapping if key is correct
    .map((message) => {
      if (modifiedApiResponse[message].length === 1) {
        return {
          message,
          currentListLKey: modifiedCurrentList[message],
          apiResponseKey: modifiedApiResponse[message],
          keyNeededToMap: modifiedApiResponse[message][0],
          isCurrentKeyMatching: Boolean(
            modifiedApiResponse[message][0] === modifiedCurrentList[message]
          ),
        };
      }

      const neoKey = modifiedApiResponse[message].find((key) =>
        key.includes('neo.aag.')
      );

      return {
        message,
        currentListLKey: modifiedCurrentList[message],
        apiResponseKey: modifiedApiResponse[message],
        keyNeededToMap: neoKey,
        isCurrentKeyMatching:
          Boolean(neoKey === modifiedCurrentList[message]) ||
          modifiedApiResponse[message].includes(modifiedCurrentList[message]),
      };
    })
    // Getting all the keys that are not matched correctly
    .filter((obj) => !obj.isCurrentKeyMatching);

  // Matching the correct keys from API response
  keysFromCurrentListExistInApiResponse.forEach((obj) => {
    const sodKey = obj.apiResponseKey.find((key) => key.includes('sod'));
    const labelKey = obj.apiResponseKey.find((key) => key.includes('label'));
    const certsKey = obj.apiResponseKey.find((key) => key.includes('CERTS'));

    if (sodKey) {
      modifiedCurrentList[obj.message] = sodKey;
    } else if (labelKey) {
      modifiedCurrentList[obj.message] = labelKey;
    } else if (certsKey) {
      modifiedCurrentList[obj.message] = certsKey;
    } else {
      const [option] = obj.apiResponseKey[0];

      modifiedCurrentList[obj.message] = option;
    }
  });

  // Messages that are not coming from API
  const keysFromCurrentListNeedToBeAddediInDB = Object.keys(modifiedCurrentList)
    .filter((message) => {
      if (message in modifiedApiResponse) {
        return false;
      }

      return true;
    })
    .reduce<Record<string, string>>((acc, message) => {
      acc[message] = modifiedCurrentList[message];

      return acc;
    }, {});

  const keysChangedInCurrentList = Object.keys(currentList).reduce<
    Record<string, { current: string; updateTo: string }>
  >((diff, message) => {
    if (currentList[message] !== modifiedCurrentList[message]) {
      diff[message] = {
        current: currentList[message],
        updateTo: modifiedCurrentList[message],
      };
    }

    return diff;
  }, {});

  if (Object.keys(keysChangedInCurrentList).length > 0) {
    logInDevelopment('No keys are pending to be added in DB');

    return;
  }

  logInDevelopment('Localization Stats', {
    ...(Object.keys(keysChangedInCurrentList).length > 0 && {
      updated_invertedMessages_as_per_retrieveMessages: modifiedCurrentList,
      changes_made_from_current_invertedMessages: keysChangedInCurrentList,
    }),
    key_value_pair_need_to_be_added_in_DB_for_localization:
      keysFromCurrentListNeedToBeAddediInDB,
  });

  // ▣ ★ ⚠
};

/**
 * Triggers a browser download for a given Blob with a suggested filename.
 * Falls back to object URL anchor click; cleans up the URL after use.
 */
export const downloadBlobAsFile = (blob: Blob, fileName: string) => {
  try {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    logInDevelopment('Failed to export: ', e);
  }
};

/**
 * Returns the first non-empty trimmed string or a number coerced to string.
 */
export function formatDisply(
  ...candidates: (string | number | null | undefined)[]
): string | undefined {
  const match = candidates.find((v) => {
    if (typeof v === 'number') {
      return true;
    }

    if (typeof v === 'string' && v.trim().length > 0) {
      return true;
    }

    return false;
  });

  if (typeof match === 'number') {
    return String(match);
  }

  if (typeof match === 'string') {
    return match.trim();
  }

  return undefined;
}

/**
 * Formats multi-value arrays into "a, b" and returns remaining count as +N.
 * Always returns a display string and a non-negative remaining count.
 */
export const formatMulti = (
  values: unknown[]
): { displayText: string; remaining: number } => {
  const list = Array.isArray(values)
    ? values.map((v) => String(v)).filter((s) => s.trim().length > 0)
    : [];

  if (list.length === 0) {
    return { displayText: '-', remaining: 0 };
  }

  if (list.length <= 2) {
    return { displayText: list.join(', '), remaining: 0 };
  }

  return {
    displayText: `${list.slice(0, 2).join(', ')}`,
    remaining: list.length - 2,
  };
};

// MM:SS formatter for seconds; clamps to non-negative
export const formatMmSs = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');

  return `${mm}:${ss}`;
};

// Backwards-compatible alias
export const formatTime = (seconds: number): string => formatMmSs(seconds);

// Timestamp coercion helper used by multiple modules
export const toTimestamp = (v: unknown): number => {
  if (typeof v === 'number') {
    return v;
  }

  if (v instanceof Date) {
    return v.getTime();
  }

  if (typeof v === 'string') {
    const t = Date.parse(v);

    return Number.isFinite(t) ? t : 0;
  }

  return 0;
};

// Shared date/time range formatters used by Request Details
export const formatRange = (
  startIso: string,
  endIso: string,
  tz: string
): string => {
  const start = new Date(startIso);
  const end = new Date(endIso);

  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  const fmt = new Intl.DateTimeFormat(undefined, opts);
  const startStr = fmt.format(start);
  const endStr = fmt.format(end);

  return `${startStr} (${tz}) to ${endStr} (${tz})`;
};

const isValidIanaTimeZone = (candidate: string): boolean => {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: candidate }).format(
      new Date()
    );

    return true;
  } catch {
    return false;
  }
};

/**
 * Parses common UTC/GMT offset string formats into minutes.
 *
 * Supported examples:
 * - "UTC+05:30", "GMT-04:00", "UTC+0530", "UTC+5", "+05:30", "-0400"
 *
 * Returns offset minutes east of UTC (e.g. +330 for UTC+05:30) or null when not an offset.
 */
export const parseUtcOffsetMinutes = (label: string): number | null => {
  const trimmed = String(label).trim();
  const upper = trimmed.toUpperCase();

  if (upper === 'Z' || upper === 'UTC' || upper === 'GMT') {
    return 0;
  }

  const m =
    /^(?:UTC|GMT)?\s*([+-])\s*(\d{1,2})(?::?(\d{2}))?\s*$/.exec(upper) ??
    /^([+-])\s*(\d{1,2})(?::?(\d{2}))?\s*$/.exec(upper);

  if (!m) {
    return null;
  }

  const sign = m[1] === '-' ? -1 : 1;
  const hours = Number(m[2]);
  const minutes = m[3] ? Number(m[3]) : 0;

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }

  // Intl generally supports offsets up to ±14:00; enforce basic sanity.
  if (hours < 0 || hours > 14 || minutes < 0 || minutes >= 60) {
    return null;
  }

  return sign * (hours * 60 + minutes);
};

export const resolveTimeZoneForIntl = (
  label: string
): { timeZone: string; offsetMinutes: number } => {
  const trimmed = String(label).trim();

  if (!trimmed) {
    return { timeZone: 'UTC', offsetMinutes: 0 };
  }

  // 1) Fixed offsets (e.g. UTC+05:30). Intl can't format arbitrary offsets directly,
  // so callers should format in UTC and shift the date by offsetMinutes.
  const offsetMinutes = parseUtcOffsetMinutes(trimmed);

  if (offsetMinutes != null && offsetMinutes !== 0) {
    return { timeZone: 'UTC', offsetMinutes };
  }

  const upper = trimmed.toUpperCase();

  if (upper === 'Z' || upper === 'UTC' || upper === 'GMT') {
    return { timeZone: 'UTC', offsetMinutes: 0 };
  }

  // 2) IANA zones from backend (preferred) - validate and use directly.
  if (trimmed.includes('/') && isValidIanaTimeZone(trimmed)) {
    return { timeZone: trimmed, offsetMinutes: 0 };
  }

  // 3) Abbreviations (ambiguous). We map only a curated set we expect from backend.
  // If backend expands coverage, prefer switching payloads to IANA (e.g. "Asia/Kolkata")
  // or fixed offsets (e.g. "UTC+05:30") to avoid ambiguity.
  const abbrToIana: Record<string, string | undefined> = {
    IST: 'Asia/Kolkata',
    JST: 'Asia/Tokyo',
    KST: 'Asia/Seoul',
    SGT: 'Asia/Singapore',
    HKT: 'Asia/Hong_Kong',
    ICT: 'Asia/Bangkok',
    PKT: 'Asia/Karachi',
    WIB: 'Asia/Jakarta',
    PHT: 'Asia/Manila',
    GST: 'Asia/Dubai',
    // Common US abbreviations (still ambiguous globally, but useful in practice):
    PST: 'America/Los_Angeles',
    PDT: 'America/Los_Angeles',
    MST: 'America/Denver',
    MDT: 'America/Denver',
    CST: 'America/Chicago',
    CDT: 'America/Chicago',
    EST: 'America/New_York',
    EDT: 'America/New_York',
  };

  const mapped = abbrToIana[upper];

  if (mapped && isValidIanaTimeZone(mapped)) {
    return { timeZone: mapped, offsetMinutes: 0 };
  }

  // 4) Last-chance: if the raw label is a valid Intl timeZone, use it; else fall back.
  if (isValidIanaTimeZone(trimmed)) {
    return { timeZone: trimmed, offsetMinutes: 0 };
  }

  return { timeZone: 'UTC', offsetMinutes: 0 };
};

export const getSafeIanaTimeZone = (label: string): string =>
  resolveTimeZoneForIntl(label).timeZone;

export const formatRangeInTimeZone = (
  startIso: string,
  endIso: string,
  timeZone: string
): string => {
  const { timeZone: ianaTimeZone, offsetMinutes } =
    resolveTimeZoneForIntl(timeZone);

  const start = new Date(startIso);
  const end = new Date(endIso);

  const startForFmt =
    offsetMinutes !== 0
      ? new Date(start.getTime() + offsetMinutes * 60_000)
      : start;
  const endForFmt =
    offsetMinutes !== 0
      ? new Date(end.getTime() + offsetMinutes * 60_000)
      : end;

  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: ianaTimeZone,
  };

  const fmt = new Intl.DateTimeFormat(undefined, opts);
  const startStr = fmt.format(startForFmt);
  const endStr = fmt.format(endForFmt);

  return `${startStr} (${timeZone}) to ${endStr} (${timeZone})`;
};

/**
 * Gets browser timezone abbreviation (e.g., IST, PST, EST).
 * Uses a mapping of common timezone IDs to abbreviations, with fallback
 * to Intl.DateTimeFormat when the timezone is not in the map.
 *
 * @param date - Date object to use for timezone formatting
 * @returns Timezone abbreviation string, or empty string if unable to determine
 */
export const getBrowserTimezone = (date: Date): string => {
  try {
    // Get browser's timezone ID (e.g., "Asia/Kolkata", "Asia/Calcutta", "America/Los_Angeles")
    const resolvedOptions = Intl.DateTimeFormat().resolvedOptions();
    const timeZoneId = resolvedOptions.timeZone || '';

    // Map common timezone IDs to their abbreviations
    // Note: Some browsers use "Asia/Calcutta" (old name) instead of "Asia/Kolkata"
    const timeZoneAbbrMap: Record<string, string> = {
      // Asia - India (both old and new names)
      'Asia/Kolkata': 'IST',
      'Asia/Calcutta': 'IST',
      // Asia - Other
      'Asia/Dubai': 'GST',
      'Asia/Singapore': 'SGT',
      'Asia/Tokyo': 'JST',
      'Asia/Shanghai': 'CST',
      'Asia/Hong_Kong': 'HKT',
      'Asia/Bangkok': 'ICT',
      'Asia/Dhaka': 'BST',
      'Asia/Karachi': 'PKT',
      'Asia/Jakarta': 'WIB',
      'Asia/Manila': 'PHT',
      'Asia/Seoul': 'KST',
      // Americas
      'America/New_York': 'EST',
      'America/Chicago': 'CST',
      'America/Denver': 'MST',
      'America/Los_Angeles': 'PST',
      'America/Phoenix': 'MST',
      'America/Toronto': 'EST',
      'America/Vancouver': 'PST',
      'America/Mexico_City': 'CST',
      'America/Sao_Paulo': 'BRT',
      'America/Buenos_Aires': 'ART',
      // Europe
      'Europe/London': 'GMT',
      'Europe/Paris': 'CET',
      'Europe/Berlin': 'CET',
      'Europe/Rome': 'CET',
      'Europe/Madrid': 'CET',
      'Europe/Amsterdam': 'CET',
      'Europe/Stockholm': 'CET',
      'Europe/Zurich': 'CET',
      'Europe/Vienna': 'CET',
      'Europe/Brussels': 'CET',
      'Europe/Dublin': 'GMT',
      'Europe/Lisbon': 'WET',
      'Europe/Athens': 'EET',
      'Europe/Moscow': 'MSK',
      // Australia
      'Australia/Sydney': 'AEDT',
      'Australia/Melbourne': 'AEDT',
      'Australia/Brisbane': 'AEST',
      'Australia/Perth': 'AWST',
      'Australia/Adelaide': 'ACDT',
      'Australia/Darwin': 'ACST',
      // Others
      'Pacific/Auckland': 'NZDT',
      'Pacific/Honolulu': 'HST',
      'Africa/Johannesburg': 'SAST',
      'Africa/Cairo': 'EET',
    };

    // First, try to get abbreviation from the map
    const mappedAbbr = timeZoneAbbrMap[timeZoneId];

    if (mappedAbbr) {
      return mappedAbbr;
    }

    // Fallback 1: Try to extract abbreviation from Intl.DateTimeFormat with 'short' format
    // This sometimes works better than the map for edge cases
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZoneId,
        timeZoneName: 'short',
      });
      const parts = formatter.formatToParts(date);
      const timeZonePart = parts.find((part) => part.type === 'timeZoneName');

      // If we get a valid abbreviation (not GMT offset), use it
      if (timeZonePart?.value && !timeZonePart.value.startsWith('GMT')) {
        return timeZonePart.value;
      }
    } catch {
      // Ignore and continue to next fallback
    }

    // Fallback 2: Generate abbreviation from long timezone name
    // Takes first letter of each word (e.g., "Pacific Standard Time" -> "PST")
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZoneId,
        timeZoneName: 'long',
      });
      const parts = formatter.formatToParts(date);
      const timeZonePart = parts.find((part) => part.type === 'timeZoneName');

      if (timeZonePart?.value) {
        const abbreviation = timeZonePart.value
          .split(' ')
          .map((w) => w.charAt(0))
          .join('')
          .toUpperCase();

        // Only return if it looks like a valid abbreviation (2-4 characters)
        if (abbreviation.length >= 2 && abbreviation.length <= 4) {
          return abbreviation;
        }
      }
    } catch {
      // Ignore and continue to final fallback
    }

    // Final fallback: Return empty string to avoid showing city names
    return '';
  } catch {
    return '';
  }
};
